import { create } from "zustand";
import {
  DocsApiError,
  deleteDoc,
  downloadDoc,
  listDocs,
  listTiers,
  searchDocs,
  uploadDoc,
  type DocBlob,
  type SearchHit,
  type TierAccess,
} from "@/lib/docs-api";

/**
 * Documents store — the SPA-side state for the interim document-storage
 * feature. Wraps `@/lib/docs-api` (the BFF client) with the tier list,
 * the current tier's blob listing, search results, and per-action
 * loading/error flags so the panel UI stays a thin renderer.
 *
 * Unlike `useAssetLocker`, there is NO local IDB cache and NO seeding:
 * the BFF + Azure Storage are the single source of truth (the whole point
 * of the feature is that bytes live server-side, brokered by the BFF). A
 * reload re-fetches; nothing persists in the browser.
 *
 * Authorization is the BFF's job — every method here simply calls the API
 * and surfaces the error. A 403 means the signed-in user's role doesn't
 * permit the action for that tier (the UI also gates via `@/lib/auth/can`,
 * but that's cosmetic; the BFF is the boundary).
 */

type Status = "idle" | "loading" | "error";

function messageOf(err: unknown): string {
  if (err instanceof DocsApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Request failed";
}

type DocumentsState = {
  /** Tiers the signed-in user can see, with their permitted actions. */
  tiers: TierAccess[];
  /** The tier currently being browsed (null until tiers load). */
  activeTier: string | null;
  /** Virtual-folder prefix within the active tier. */
  prefix: string;
  /** Blobs in the active tier under `prefix`. */
  blobs: DocBlob[];
  /** Most recent AI-search results + which collections were searched. */
  searchResults: SearchHit[];
  collectionsSearched: string[];

  tiersStatus: Status;
  blobsStatus: Status;
  searchStatus: Status;
  /** Set true while an upload/delete mutation is in flight. */
  mutating: boolean;
  error: string | null;

  /** Load the tier list and select the first readable tier. */
  loadTiers: () => Promise<void>;
  /** Switch the browsed tier (resets prefix) and load its blobs. */
  selectTier: (tier: string) => Promise<void>;
  /** Set the virtual-folder prefix and reload blobs for the active tier. */
  setPrefix: (prefix: string) => Promise<void>;
  /** Reload the active tier's blob listing. */
  refresh: () => Promise<void>;
  /** Upload a file to the active tier under `path`, then refresh. */
  upload: (path: string, file: Blob) => Promise<boolean>;
  /** Delete a blob from the active tier, then refresh. */
  remove: (path: string) => Promise<boolean>;
  /** Download a blob's bytes (no state change; caller saves/previews). */
  download: (path: string) => Promise<Blob>;
  /** Run an AI search across the user's readable tiers. */
  search: (query: string, topK?: number) => Promise<void>;
  /** Clear the last search. */
  clearSearch: () => void;
};

export const useDocuments = create<DocumentsState>((set, get) => ({
  tiers: [],
  activeTier: null,
  prefix: "",
  blobs: [],
  searchResults: [],
  collectionsSearched: [],
  tiersStatus: "idle",
  blobsStatus: "idle",
  searchStatus: "idle",
  mutating: false,
  error: null,

  loadTiers: async () => {
    set({ tiersStatus: "loading", error: null });
    try {
      const tiers = await listTiers();
      set({ tiers, tiersStatus: "idle" });
      // Auto-select the first readable tier so the panel has something to
      // show on open. selectTier loads its blobs.
      const first = tiers[0]?.tier ?? null;
      if (first) {
        await get().selectTier(first);
      } else {
        set({ activeTier: null, blobs: [] });
      }
    } catch (err) {
      set({ tiersStatus: "error", error: messageOf(err) });
    }
  },

  selectTier: async (tier) => {
    set({ activeTier: tier, prefix: "" });
    await get().refresh();
  },

  setPrefix: async (prefix) => {
    set({ prefix });
    await get().refresh();
  },

  refresh: async () => {
    const { activeTier, prefix } = get();
    if (!activeTier) return;
    set({ blobsStatus: "loading", error: null });
    try {
      const blobs = await listDocs(activeTier, prefix);
      set({ blobs, blobsStatus: "idle" });
    } catch (err) {
      set({ blobsStatus: "error", error: messageOf(err) });
    }
  },

  upload: async (path, file) => {
    const { activeTier } = get();
    if (!activeTier) return false;
    set({ mutating: true, error: null });
    try {
      await uploadDoc(activeTier, path, file);
      await get().refresh();
      set({ mutating: false });
      return true;
    } catch (err) {
      set({ mutating: false, error: messageOf(err) });
      return false;
    }
  },

  remove: async (path) => {
    const { activeTier } = get();
    if (!activeTier) return false;
    set({ mutating: true, error: null });
    try {
      await deleteDoc(activeTier, path);
      await get().refresh();
      set({ mutating: false });
      return true;
    } catch (err) {
      set({ mutating: false, error: messageOf(err) });
      return false;
    }
  },

  download: async (path) => {
    const { activeTier } = get();
    if (!activeTier) throw new DocsApiError(400, "No tier selected");
    return await downloadDoc(activeTier, path);
  },

  search: async (query, topK = 5) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    set({ searchStatus: "loading", error: null });
    try {
      const res = await searchDocs(trimmed, topK);
      set({
        searchResults: res.results,
        collectionsSearched: res.collections_searched,
        searchStatus: "idle",
      });
    } catch (err) {
      set({ searchStatus: "error", error: messageOf(err) });
    }
  },

  clearSearch: () =>
    set({ searchResults: [], collectionsSearched: [], searchStatus: "idle" }),
}));
